// Browser fallback settings share the server's JSON source. Stimulus values
// remain authoritative when markup provides overrides.
import thresholds from "../../../config/aero/display_thresholds.json"

export const NEUTRAL_COLOR_CLASS = thresholds.neutral_color_class
export const IMPROVEMENT_COLOR_CLASS = thresholds.improvement_color_class
export const REGRESSION_COLOR_CLASS = thresholds.regression_color_class
export const DEFAULT_CDA_COLOR_THRESHOLD = thresholds.cda_color_threshold
export const DEFAULT_WATTS_COLOR_THRESHOLD = thresholds.watts_color_threshold
export const DEFAULT_CDA_DISPLAY_PRECISION = thresholds.cda_display_precision

export function colorClassFor(value, threshold = 0, {
  negativeIsImprovement = true,
  neutralClass = NEUTRAL_COLOR_CLASS,
  improvementClass = IMPROVEMENT_COLOR_CLASS,
  regressionClass = REGRESSION_COLOR_CLASS
} = {}) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return neutralClass
  if (numeric === 0 || Math.abs(numeric) < threshold) return neutralClass
  const improvement = negativeIsImprovement ? numeric < 0 : numeric > 0
  return improvement ? improvementClass : regressionClass
}

export function cdaColorClass(value) {
  return colorClassFor(value, DEFAULT_CDA_COLOR_THRESHOLD)
}

export function wattsColorClass(value) {
  return colorClassFor(value, DEFAULT_WATTS_COLOR_THRESHOLD)
}

// Decimal half-up canon shared with Aero::Display. Applying the rule to the
// magnitude makes negative ties round away from zero too (-1.25 -> -1.3).
export function roundHalfUp(value, decimals = 0) {
  if (value == null || (typeof value === "string" && value.trim() === "")) return null

  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null

  const factor = 10 ** decimals
  const scaled = Math.abs(numeric) * factor
  const tolerance = Number.EPSILON * Math.max(1, scaled) * 4
  const roundedMagnitude = Math.floor(scaled + 0.5 + tolerance) / factor
  return numeric < 0 ? -roundedMagnitude : roundedMagnitude
}

export function formatDecimalMetric(value, decimals = 1) {
  const rounded = roundHalfUp(value, decimals)
  if (rounded == null) return ""

  return rounded.toFixed(decimals)
}

// Signed metrics keep a plus for zero/positive values and normalize rounded
// negative zero to +0.0. Unit, when present, follows one space later.
export function formatSignedMetric(value, decimals = 1, unit = null) {
  const rounded = roundHalfUp(value, decimals)
  if (rounded == null) return ""

  const fixed = Math.abs(rounded).toFixed(decimals)
  const formatted = rounded < 0 ? `-${fixed}` : `+${fixed}`
  return unit ? `${formatted} ${unit}` : formatted
}
